import { makeStyles, Alert, Button, Grid, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import { formatCurrency, formatName, getQuoteLoanApplication } from '@paintscout/util/builder'
import startCase from 'lodash/startCase'
import type { ReactNode } from 'react'
import React, { useContext, useState } from 'react'
import type { LoanApplicationStatus } from '@paintscout/wisetack'
import { useWisetack, WisetackStatus } from '..'
import { getWisetackAlerts } from '../getWisetackAlerts'
import { getWisetackApplicationBody } from './getWisetackApplicationBody'
import { QuoteContext } from '../../context'

const useStyles = makeStyles((theme: Theme) => ({
  dialogLogo: {
    width: theme.typography.pxToRem(120),
    objectFit: 'contain',
    margin: theme.spacing(1, 0)
  },
  clickable: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  root: {
    background: theme.palette.grey['100'],
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    '@media print': {
      display: 'none'
    }
  },
  logo: {
    margin: theme.spacing(1, 0),
    width: theme.typography.pxToRem(120),
    objectFit: 'contain'
  },
  content: {
    paddingRight: theme.spacing(1)
  },
  alerts: {
    marginTop: theme.spacing(3)
  },
  applicationButton: {
    padding: 0
  }
}))

export default function WisetackApplication({
  inPresentation,
  isPreview
}: {
  inPresentation?: boolean
  // Merchant-facing preview (QuotePreviewDialog): render as the customer sees it, but the
  // application button must not initiate financing. (ENG-833)
  isPreview?: boolean
}) {
  const { options } = useClientOptions()
  const { user, preferences } = useUser()
  const { quote, save: saveQuote, wisetackPromo } = useContext(QuoteContext)

  const contact = quote.contact

  const {
    hasWisetack,
    merchantSignupDate,
    showOnOldDocuments,
    maximumLoanAmount,
    handleTextLoanApplication,
    handleGetLoanApplication
  } = useWisetack({
    user,
    options,
    preferences
  })

  // Don't show wisetack stuff on estimates/invoices made before the merchant signed up unless overridden\
  // Or if something went wrong getting the promo
  if (
    !hasWisetack ||
    !wisetackPromo ||
    !merchantSignupDate ||
    (merchantSignupDate.isAfter(quote?.dates?.created) && !showOnOldDocuments)
  ) {
    return null
  }

  const handleLoanMerchant = async () => {
    if (!!saveQuote && !quote._rev && !!quote.contact) {
      await saveQuote(quote).then(handleTextLoanApplication)
    } else {
      handleTextLoanApplication(quote)
    }
  }

  const handleLoanCustomer = async () => {
    handleGetLoanApplication(quote)
  }

  const getButtonLabel = () => {
    if (!inPresentation) {
      return 'Send Application'
    }

    if (loanStatus === undefined) {
      return 'See options'
    }

    // Wisetack lets a declined customer apply again (matches WisetackApplicationNext).
    if (loanStatus === 'DECLINED') {
      return 'Apply Again'
    }

    if (
      loanStatus === 'SETTLED' ||
      (approvedAmount && approvedAmount > quote?.totals?.balance_due && payouts?.length < 2)
    ) {
      return 'Apply Again'
    }

    if (loanStatus === 'LOAN TERMS ACCEPTED') {
      return 'Release Funds'
    }

    return 'Continue Application'
  }

  const loanApplication = getQuoteLoanApplication({ contact, quote })
  const loanStatus = loanApplication?.changedStatus as LoanApplicationStatus | undefined
  const maximumAmount = loanApplication?.maximumAmount
  const approvedAmount = loanApplication?.approvedLoanAmount
  const payouts = loanApplication?.payouts

  const merchantTitle = 'Win more jobs by offering low monthly payments'
  const customerTitle = 'Pay over time with Wisetack'

  const getBody = () =>
    getWisetackApplicationBody({
      status: loanStatus,
      approvedAmount,
      maximumAmount,
      contactName: startCase(formatName(contact)) || 'Your Customer',
      inPresentation,
      formatAmount: (value) => formatCurrency({ options, value })
    })

  // merchant financing
  if (!inPresentation) {
    if (!quote?.totals?.balance_due || quote.totals.balance_due < 500) {
      return null
    }
    return (
      <BaseApplication
        handleApply={handleLoanMerchant}
        hideButton={loanStatus === 'LOAN CONFIRMED'}
        buttonLabel={getButtonLabel()}
        title={merchantTitle}
        body={getBody()}
        Status={
          loanStatus ? (
            <WisetackStatus hideIcon variant="loanApplication" status={loanStatus} amount={approvedAmount} />
          ) : null
        }
        alertContent={getWisetackAlerts({ quote, options, maximumLoanAmount, inPresentation })}
      />
    )
  }

  // customer financing
  if (inPresentation) {
    if (!quote?.totals?.balance_due || quote.totals.balance_due < 500) {
      return null
    }
    if (!quote?.contact) {
      return null
    }

    return (
      <BaseApplication
        handleApply={handleLoanCustomer}
        // In the merchant preview the button is shown for fidelity but disabled so it can't
        // initiate financing (ENG-833).
        disabled={isPreview}
        hideButton={loanStatus === 'LOAN CONFIRMED'}
        buttonLabel={getButtonLabel()}
        title={customerTitle}
        body={getBody()}
        Status={loanStatus ? <WisetackStatus hideIcon variant="loanApplication" status={loanStatus} /> : null}
        alertContent={getWisetackAlerts({ quote, options, maximumLoanAmount, inPresentation })}
      />
    )
  }

  return null
}

export interface WisetackApplicationProps {
  title: string
  body?: string
  alertContent?: string[]
  buttonLabel: string
  hideButton?: boolean
  disabled?: boolean
  Status?: ReactNode
  handleApply: () => Promise<void>
}

export function BaseApplication({
  title,
  body,
  buttonLabel,
  alertContent,
  Status,
  hideButton,
  disabled,
  handleApply
}: WisetackApplicationProps) {
  const classes = useStyles()

  const [loading, setLoading] = useState(false)

  async function onApply() {
    setLoading(true)
    await handleApply()
    setLoading(false)
  }

  return (
    <Grid container className={classes.root} justifyContent="space-between" alignItems="center">
      <Grid item>
        <img
          className={classes.logo}
          alt="Wisetack"
          src={'https://paintscout.s3.us-east-1.amazonaws.com/providers/wisetack.webp'}
        />
      </Grid>
      <Grid item>{Status}</Grid>
      <Grid item xs={12}>
        <Typography variant="h3">
          <strong>{title}</strong>
        </Typography>
      </Grid>
      <Grid item xs={12} sm={7}>
        {!!body && (
          <Typography variant="body2">
            <strong className={classes.content}>{body}</strong>
          </Typography>
        )}
      </Grid>
      {!hideButton && (
        <Grid item>
          <Button
            className={classes.applicationButton}
            id="allow-propagation"
            loading={loading}
            disabled={disabled}
            onClick={onApply}
            variant="underline"
          >
            {buttonLabel}
          </Button>
        </Grid>
      )}

      {alertContent?.length > 0 && (
        <div className={classes.alerts}>
          {alertContent.map((alert, index) => (
            <Grid key={index} item xs={12}>
              <Alert severity="warning">{alert}</Alert>
            </Grid>
          ))}
        </div>
      )}
    </Grid>
  )
}
